/*
 * ddd-membership.js, Storefront JS for /pages/membership.
 *
 * Lives in the Shopify theme as assets/ddd-membership.js. Source-of-truth
 * copy is in this repo (shopify-theme/assets/ddd-membership.js); upload
 * to Shopify via the theme code editor when changing.
 *
 * What it does:
 *   - Monthly/Annual toggle on the tier cards (unchanged from v1).
 *   - "Choose plan" / "Sign up free" buttons: check for a stored
 *     Supabase session. If none, open an inline modal with email +
 *     password Sign-up / Log-in tabs. On success store the session in
 *     localStorage and continue the flow.
 *   - Paid tiers: POST to /apps/dpp/checkout-session?plan=...&token=...
 *     and redirect to the returned Stripe Checkout URL.
 *   - Free tier: signup creates the user; the Free membership is
 *     bootstrapped lazily on first /memberships/status call, so we
 *     just show a "You're in" success state and let them browse.
 *
 * Why custom auth: Shopify removed classic email+password customer
 * accounts in 2024 (only Shop Pay / one-time-code / social remain). For
 * a paid membership site we want a real password flow, so we run
 * Supabase Auth via the App Proxy.
 */
(function () {
  "use strict";

  // ---------- Config ----------
  var APP_PROXY_PATH =
    (window.DDD_CONFIG && window.DDD_CONFIG.appProxyPath) || "dpp";
  var SESSION_KEY = "ddd_session_v1";
  var SESSION_LEEWAY_SEC = 60; // refresh window before token expiry
  // Auth goes direct to the backend (CORS), not the /apps/dpp proxy path,
  // which ad/privacy blockers block. checkout-session stays on the proxy.
  var AUTH_BASE =
    ((window.DDD_CONFIG && window.DDD_CONFIG.downloadBase) ||
      "https://download.thedigitaldesigndock.com") + "/membership/auth";

  function proxyUrl(path) {
    return "/apps/" + APP_PROXY_PATH + path;
  }

  // ---------- Session storage ----------
  // Session helpers delegate to the shared ddd-auth.js (window.DDDAuth),
  // loaded first in <head>, so this file and ddd-product.js can never drift
  // on the session layer again (that drift caused the login bug).
  function getSession() {
    return window.DDDAuth.getSession();
  }

  function setSession(payload) {
    return window.DDDAuth.setSession(payload);
  }

  function clearSession() {
    return window.DDDAuth.clearSession();
  }

  // ---------- Tier toggle (monthly / annual) ----------
  var toggleBtns = document.querySelectorAll(".ddd-membership__toggle-btn");

  function getActiveBilling() {
    var active = document.querySelector(".ddd-membership__toggle-btn.is-active");
    return (active && active.dataset.billing) || "monthly";
  }

  function setBilling(period) {
    toggleBtns.forEach(function (btn) {
      var isActive = btn.dataset.billing === period;
      btn.classList.toggle("is-active", isActive);
      btn.setAttribute("aria-selected", isActive ? "true" : "false");
    });
    document
      .querySelectorAll("[data-price-monthly], [data-per-monthly]")
      .forEach(function (el) {
        el.classList.toggle("is-hidden", period !== "monthly");
      });
    document
      .querySelectorAll("[data-price-annual], [data-per-annual]")
      .forEach(function (el) {
        el.classList.toggle("is-hidden", period !== "annual");
      });
    document.querySelectorAll("[data-saving]").forEach(function (el) {
      el.classList.toggle("is-hidden", period !== "annual");
    });
  }

  toggleBtns.forEach(function (btn) {
    btn.addEventListener("click", function () {
      setBilling(btn.dataset.billing);
    });
  });

  var sectionRoot = document.querySelector(".ddd-membership");
  if (sectionRoot) {
    var defaultBilling = sectionRoot.dataset.defaultBilling || "monthly";
    setBilling(defaultBilling);
  }

  // ---------- Currency (follow Shopify's active presentment currency) ----------
  // The store's native country/currency picker sets Shopify.currency.active
  // and auto-detects it by geo. We render the membership prices in that
  // currency and pass it into checkout, so what's shown always matches what's
  // charged. We only hold live Stripe prices in GBP/USD/EUR; anything else
  // falls back to GBP so checkout can't hit a missing price. The Shopify
  // picker reloads the page on change, so reading it once on load is enough.
  var CURRENCY_SYMBOLS = { GBP: "£", USD: "$", EUR: "€" };
  var SUPPORTED_CURRENCIES = ["GBP", "USD", "EUR"];

  function activeCurrency() {
    var c =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "";
    c = String(c).toUpperCase();
    return SUPPORTED_CURRENCIES.indexOf(c) >= 0 ? c : "GBP";
  }

  function formatMoney(currency, minorAmount) {
    var sym = CURRENCY_SYMBOLS[currency] || "";
    var major = minorAmount / 100;
    return sym + (major % 1 === 0 ? String(major) : major.toFixed(2));
  }

  function applyCurrencyPrices() {
    var currency = activeCurrency();
    window.__dddCurrency = currency; // startCheckout reads this
    var base =
      (window.DDD_CONFIG && window.DDD_CONFIG.downloadBase) ||
      "https://download.thedigitaldesigndock.com";
    fetch(base + "/membership/plans", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var byName = {};
        ((data && data.plans) || []).forEach(function (p) {
          byName[(p.name || "").toLowerCase()] = p;
        });
        var maxSaving = 0;
        document.querySelectorAll(".ddd-tier[data-plan]").forEach(function (card) {
          var handle = (card.dataset.plan || "").toLowerCase();
          if (handle === "free") {
            // Free has no Stripe price, but localise the "0" symbol so it
            // matches the paid tiers (£0 / $0 / €0) instead of always $0.
            var fm = card.querySelector("[data-price-monthly]");
            var fy = card.querySelector("[data-price-annual]");
            if (fm) fm.textContent = formatMoney(currency, 0);
            if (fy) fy.textContent = formatMoney(currency, 0);
            return;
          }
          var plan = byName[handle];
          if (!plan || !plan.prices) return;
          function priceFor(intervalDb) {
            return plan.prices.filter(function (pr) {
              return (
                (pr.currency || "").toUpperCase() === currency &&
                (pr.interval || "").toLowerCase() === intervalDb
              );
            })[0];
          }
          var m = priceFor("month");
          var y = priceFor("year");
          var mEl = card.querySelector("[data-price-monthly]");
          var yEl = card.querySelector("[data-price-annual]");
          var saveEl = card.querySelector("[data-saving]");
          if (m && mEl) mEl.textContent = formatMoney(currency, m.unit_amount);
          if (y && yEl) yEl.textContent = formatMoney(currency, y.unit_amount);
          if (m && y) {
            var saving = m.unit_amount * 12 - y.unit_amount;
            if (saving > maxSaving) maxSaving = saving;
            if (saveEl) {
              if (saving > 0) saveEl.textContent = "Save " + formatMoney(currency, saving);
              else saveEl.classList.add("is-hidden");
            }
          }
        });
        // Localise the Annual toggle pill ("save up to £X") to the biggest
        // per-year saving across the paid tiers.
        var savePill = document.querySelector(".ddd-membership__save");
        if (savePill && maxSaving > 0) {
          savePill.textContent = "save up to " + formatMoney(currency, maxSaving);
        }
        // Footnote: swap the hardcoded "Prices in USD" to the active currency.
        var foot = document.querySelector(".ddd-membership__footnote");
        if (foot) {
          foot.textContent = foot.textContent.replace(/Prices in [A-Z]{3}\b/, "Prices in " + currency);
        }
      })
      .catch(function () {
        /* leave the server-rendered prices in place on any failure */
      });
  }
  applyCurrencyPrices();

  // ---------- Signed-in member banner ----------
  // When a member is logged in, greet them with their current plan + how many
  // downloads they have left, instead of just showing the pricing table as if
  // they were a stranger. Uses the same /membership/auth/status the rest of the
  // site does (direct host, not /apps/dpp).
  // Stripe Billing Portal: lets a paying member change tier (Stripe prorates
  // mid-cycle automatically), update card, or cancel — so we build none of
  // that maths. Free members never reach this (no subscription to manage).
  function openPortal(trigger) {
    var session = getSession();
    if (!session || !session.accessToken) return;
    var base =
      (window.DDD_CONFIG && window.DDD_CONFIG.downloadBase) ||
      "https://download.thedigitaldesigndock.com";
    var orig = trigger ? trigger.textContent : null;
    if (trigger) {
      trigger.disabled = true;
      trigger.textContent = "Opening…";
    }
    fetch(base + "/membership/portal", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: "Bearer " + session.accessToken,
      },
      body: JSON.stringify({ returnUrl: window.location.origin + "/pages/membership" }),
    })
      .then(function (r) { return r.json().then(function (p) { return { ok: r.ok, p: p }; }); })
      .then(function (res) {
        if (res.ok && res.p && res.p.url) {
          window.location.href = res.p.url;
          return;
        }
        if (trigger) { trigger.disabled = false; trigger.textContent = orig; }
        window.alert((res.p && res.p.error) || "Could not open the billing portal. Please try again.");
      })
      .catch(function () {
        if (trigger) { trigger.disabled = false; trigger.textContent = orig; }
        window.alert("Could not reach the server. Please try again.");
      });
  }

  // Reshape the tier CTAs for a signed-in member: their own tier reads
  // "Current plan" (inert); for a PAID member every other tier routes through
  // the Stripe portal (up/downgrade/cancel + proration); a FREE member still
  // checks out normally on the paid tiers.
  function adaptTierCtas(planName) {
    var current = (planName || "Free").toLowerCase();
    var isPaid = current !== "free";
    var ctas = document.querySelectorAll('[data-action="checkout"], [data-action="signup-free"]');
    Array.prototype.forEach.call(ctas, function (btn) {
      var card = btn.closest(".ddd-tier");
      var handle = ((card && card.dataset.plan) || btn.dataset.planHandle || "").toLowerCase();
      if (!handle) return;
      if (handle === current) {
        btn.textContent = "Current plan";
        btn.dataset.dddCta = "current";
        btn.disabled = true;
        btn.style.opacity = "0.55";
        btn.style.cursor = "default";
      } else if (isPaid) {
        btn.textContent = handle === "free" ? "Switch to Free" : "Change plan";
        btn.dataset.dddCta = "portal";
      }
    });
  }

  function showMemberStatusBanner() {
    var session = getSession();
    if (!session || !session.accessToken) return;
    var root = document.querySelector(".ddd-membership");
    if (!root || document.getElementById("ddd-member-banner")) return;
    fetch(AUTH_BASE + "/status?token=" + encodeURIComponent(session.accessToken), {
      headers: { Accept: "application/json" },
    })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (!d || !d.signedIn) return;
        var plan = (d.plan && d.plan.name) || "Free";
        var usage = d.usage || {};
        var remaining = typeof usage.remaining === "number" ? usage.remaining : null;
        var interval = d.plan && d.plan.quotaInterval === "week" ? "week" : "month";
        var msg = "You're on the " + plan + " plan";
        if (remaining === -1) msg += " with unlimited downloads.";
        else if (remaining != null)
          msg += ", " + remaining + " download" + (remaining === 1 ? "" : "s") + " left this " + interval + ".";
        else msg += ".";
        if (!document.getElementById("ddd-member-banner-style")) {
          var st = document.createElement("style");
          st.id = "ddd-member-banner-style";
          st.textContent =
            ".ddd-member-banner{max-width:1120px;margin:0 auto 1.5rem;padding:.9rem 1.1rem;background:#0f172a;color:#fff;border-radius:12px;font-size:1.02rem;display:flex;flex-wrap:wrap;gap:.4rem 1rem;align-items:center;justify-content:center;text-align:center;font-family:inherit;}" +
            ".ddd-member-banner a{color:#fff;text-decoration:underline;font-weight:600;}";
          document.head.appendChild(st);
        }
        var b = document.createElement("div");
        b.id = "ddd-member-banner";
        b.className = "ddd-member-banner";
        var span = document.createElement("span");
        span.textContent = msg + " ";
        b.appendChild(span);
        var browse = document.createElement("a");
        browse.href = "/collections/all";
        browse.textContent = "Browse the catalogue";
        b.appendChild(browse);
        b.appendChild(document.createTextNode(" · "));
        var dl = document.createElement("a");
        dl.href = "/pages/my-downloads";
        dl.textContent = "My downloads";
        b.appendChild(dl);
        if (plan.toLowerCase() !== "free") {
          b.appendChild(document.createTextNode(" · "));
          var mng = document.createElement("a");
          mng.href = "#";
          mng.textContent = "Manage your plan";
          mng.addEventListener("click", function (e) { e.preventDefault(); openPortal(mng); });
          b.appendChild(mng);
        }
        root.insertBefore(b, root.firstChild);
        adaptTierCtas(plan);
      })
      .catch(function () {});
  }
  showMemberStatusBanner();

  // ---------- Auth modal (inline, no external deps) ----------
  var MODAL_ID = "ddd-auth-modal";

  function injectStyles() {
    if (document.getElementById("ddd-auth-styles")) return;
    var s = document.createElement("style");
    s.id = "ddd-auth-styles";
    s.textContent =
      "#ddd-auth-modal{display:none;position:fixed;inset:0;z-index:9999;background:rgba(15,23,42,.55);align-items:center;justify-content:center;padding:1rem;}" +
      "#ddd-auth-modal.is-open{display:flex;}" +
      "#ddd-auth-modal .ddd-auth-card{background:#fff;color:#0f172a;border-radius:14px;max-width:440px;width:100%;padding:1.5rem 1.5rem 1.25rem;box-shadow:0 20px 60px rgba(0,0,0,.3);}" +
      "#ddd-auth-modal h2{font-size:1.25rem;font-weight:600;margin:0;}" +
      "#ddd-auth-modal .ddd-auth-sub{color:#64748b;font-size:.9rem;margin:.25rem 0 1.1rem;}" +
      "#ddd-auth-modal .ddd-auth-tabs{display:flex;gap:.4rem;margin-bottom:1rem;border-bottom:1px solid #e2e8f0;padding-bottom:.5rem;}" +
      "#ddd-auth-modal .ddd-auth-tab{flex:1;background:transparent;border:1px solid transparent;border-radius:8px;padding:.45rem .6rem;font-weight:600;font-size:.9rem;color:#64748b;cursor:pointer;}" +
      "#ddd-auth-modal .ddd-auth-tab.is-active{background:#0f172a;color:#fff;}" +
      "#ddd-auth-modal label{display:block;font-size:.8rem;color:#475569;margin:0 0 .25rem;font-weight:500;}" +
      "#ddd-auth-modal input{display:block;width:100%;padding:.6rem .75rem;border:1px solid #cbd5e1;border-radius:8px;margin-bottom:.75rem;font:inherit;color:inherit;background:#fff;}" +
      "#ddd-auth-modal input:focus{outline:2px solid #6366f1;outline-offset:1px;border-color:#6366f1;}" +
      "#ddd-auth-modal .ddd-auth-submit{display:block;width:100%;padding:.7rem;background:#0f172a;color:#fff;border:none;border-radius:8px;font-weight:600;cursor:pointer;font-size:.95rem;}" +
      "#ddd-auth-modal .ddd-auth-submit:disabled{opacity:.6;cursor:wait;}" +
      "#ddd-auth-modal .ddd-auth-close{position:absolute;top:1rem;right:1rem;background:none;border:none;font-size:1.5rem;line-height:1;cursor:pointer;color:#94a3b8;}" +
      "#ddd-auth-modal .ddd-auth-error{color:#b91c1c;font-size:.85rem;margin:0 0 .75rem;display:none;}" +
      "#ddd-auth-modal .ddd-auth-error.is-visible{display:block;}" +
      "#ddd-auth-modal .ddd-auth-foot{font-size:.75rem;color:#94a3b8;margin:.85rem 0 0;text-align:center;}" +
      "#ddd-auth-modal .ddd-auth-card{position:relative;}";
    document.head.appendChild(s);
  }

  function ensureModal() {
    var existing = document.getElementById(MODAL_ID);
    if (existing) return existing;
    injectStyles();
    var modal = document.createElement("div");
    modal.id = MODAL_ID;
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.innerHTML =
      '<div class="ddd-auth-card">' +
      '<button type="button" class="ddd-auth-close" aria-label="Close">&times;</button>' +
      '<h2 data-auth-title>Create your account</h2>' +
      '<p class="ddd-auth-sub" data-auth-sub>Email and a password. Nothing else.</p>' +
      '<div class="ddd-auth-tabs" role="tablist">' +
      '<button type="button" class="ddd-auth-tab is-active" data-tab="signup" role="tab">Sign up</button>' +
      '<button type="button" class="ddd-auth-tab" data-tab="login" role="tab">Log in</button>' +
      "</div>" +
      '<form class="ddd-auth-form" novalidate>' +
      "<label>Email</label>" +
      '<input type="email" name="email" autocomplete="email" required>' +
      "<label>Password</label>" +
      '<input type="password" name="password" autocomplete="new-password" minlength="8" required>' +
      '<p class="ddd-auth-error" data-error></p>' +
      '<button type="submit" class="ddd-auth-submit" data-submit>Continue</button>' +
      "</form>" +
      '<p class="ddd-auth-foot">By continuing you agree to our <a href="/policies/terms-of-service" style="color:inherit;text-decoration:underline;">terms</a>.</p>' +
      "</div>";
    document.body.appendChild(modal);

    // Close handlers
    modal
      .querySelector(".ddd-auth-close")
      .addEventListener("click", function () {
        closeModal();
      });
    modal.addEventListener("click", function (e) {
      if (e.target === modal) closeModal();
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && modal.classList.contains("is-open")) {
        closeModal();
      }
    });

    // Tab switching
    modal.querySelectorAll(".ddd-auth-tab").forEach(function (tab) {
      tab.addEventListener("click", function () {
        switchTab(tab.dataset.tab);
      });
    });

    // Form submit
    var form = modal.querySelector(".ddd-auth-form");
    form.addEventListener("submit", function (e) {
      e.preventDefault();
      handleAuthSubmit();
    });

    return modal;
  }

  function switchTab(which) {
    var modal = ensureModal();
    modal.querySelectorAll(".ddd-auth-tab").forEach(function (t) {
      t.classList.toggle("is-active", t.dataset.tab === which);
      t.setAttribute("aria-selected", t.dataset.tab === which ? "true" : "false");
    });
    var title = modal.querySelector("[data-auth-title]");
    var sub = modal.querySelector("[data-auth-sub]");
    var input = modal.querySelector("input[name=password]");
    var submit = modal.querySelector("[data-submit]");
    setError(null);
    if (which === "login") {
      title.textContent = "Welcome back";
      sub.textContent = "Sign in to continue.";
      input.setAttribute("autocomplete", "current-password");
      submit.textContent = "Sign in";
    } else {
      title.textContent = "Create your account";
      sub.textContent = "Email and a password. Nothing else.";
      input.setAttribute("autocomplete", "new-password");
      submit.textContent = "Continue";
    }
    modal.dataset.mode = which;
  }

  function setError(msg) {
    var modal = ensureModal();
    var el = modal.querySelector("[data-error]");
    if (!msg) {
      el.classList.remove("is-visible");
      el.textContent = "";
      return;
    }
    el.classList.add("is-visible");
    el.textContent = msg;
  }

  // Outstanding intent that triggered the modal (so we can resume after auth).
  // Shape: { kind: "checkout", planHandle: "pro", interval: "monthly" } |
  //        { kind: "signup-free" }
  var pendingIntent = null;

  function openModal(intent) {
    pendingIntent = intent || null;
    var modal = ensureModal();
    switchTab("signup");
    modal.classList.add("is-open");
    var first = modal.querySelector("input[name=email]");
    if (first) setTimeout(function () { first.focus(); }, 50);
  }

  function closeModal() {
    var modal = document.getElementById(MODAL_ID);
    if (modal) modal.classList.remove("is-open");
    pendingIntent = null;
  }

  // Free-signup post-submit state. Replaces the modal form with a clear
  // "check your email" message. Modal stays open so the user definitely
  // sees that an action on their part is still required.
  function showCheckEmailState(emailAddr) {
    var modal = ensureModal();
    var card = modal.querySelector(".ddd-auth-card");
    if (!card) return;
    card.innerHTML =
      '<button type="button" class="ddd-auth-close" aria-label="Close">&times;</button>' +
      '<div style="text-align:center;padding:.5rem 0 .25rem;">' +
      '<div style="font-size:2.5rem;line-height:1;margin-bottom:.6rem;">' +
      "&#x1F4EC;" +
      "</div>" +
      '<h2 style="font-size:1.25rem;font-weight:600;margin:0 0 .5rem;">Check your email</h2>' +
      '<p style="color:#475569;font-size:.95rem;margin:0 0 1rem;">' +
      "We just sent a confirmation link to " +
      '<strong style="color:#0f172a;">' + escapeHtml(emailAddr) + "</strong>." +
      "</p>" +
      '<p style="color:#64748b;font-size:.85rem;margin:0 0 1.1rem;">' +
      "Click the link in that email to finish setting up your account. " +
      "It can take a minute to arrive and sometimes lands in spam." +
      "</p>" +
      '<button type="button" class="ddd-auth-submit" data-close>Got it</button>' +
      "</div>";
    card.querySelector(".ddd-auth-close").addEventListener("click", closeModal);
    card.querySelector("[data-close]").addEventListener("click", closeModal);
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function handleAuthSubmit() {
    var modal = ensureModal();
    var mode = modal.dataset.mode || "signup";
    var emailInput = modal.querySelector("input[name=email]");
    var pwInput = modal.querySelector("input[name=password]");
    var submit = modal.querySelector("[data-submit]");
    var email = (emailInput.value || "").trim().toLowerCase();
    var password = pwInput.value || "";

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (mode === "signup" && password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    setError(null);
    submit.disabled = true;
    submit.textContent =
      mode === "login" ? "Signing in..." : "Creating account...";

    var endpoint = mode === "login" ? "/login" : "/signup";

    // Decide signup intent based on what the user clicked to open the modal.
    // pendingIntent.kind === "checkout" → paid flow, auto-confirm so we
    //   can hand off to Stripe with zero friction.
    // Otherwise (signup-free or login) → "free" flow which sends a
    //   verification email and returns no session.
    var intent =
      pendingIntent && pendingIntent.kind === "checkout" ? "checkout" : "free";

    var requestBody = { email: email, password: password };
    if (mode === "signup") requestBody.intent = intent;

    fetch(AUTH_BASE + endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(requestBody),
    })
      .then(function (res) {
        return res.json().then(function (payload) {
          return { status: res.status, ok: res.ok, payload: payload };
        });
      })
      .then(function (r) {
        if (!r.ok || !r.payload || !r.payload.success) {
          var err = (r.payload && r.payload.error) || "Something went wrong.";
          if (r.payload && r.payload.code === "EMAIL_TAKEN") {
            setError("That email already has an account. Try logging in.");
            switchTab("login");
          } else {
            setError(err);
          }
          submit.disabled = false;
          submit.textContent = mode === "login" ? "Sign in" : "Continue";
          return;
        }

        // Free signup path: backend created an UNCONFIRMED user and sent
        // a verification email. No session yet. Switch the modal to a
        // "check your email" state and leave it open so the user clearly
        // sees what to do next.
        if (r.payload.needsVerification) {
          showCheckEmailState(r.payload.email || email);
          return;
        }

        // Paid (checkout) or login path: real session in the response.
        setSession(r.payload);
        submit.textContent = mode === "login" ? "Signed in" : "Account created";
        resumePendingIntent();
      })
      .catch(function (err) {
        setError(
          (err && err.message) ||
            "Could not reach the server. Please try again."
        );
        submit.disabled = false;
        submit.textContent = mode === "login" ? "Sign in" : "Continue";
      });
  }

  function resumePendingIntent() {
    var intent = pendingIntent;
    closeModal();
    if (!intent) return;
    if (intent.kind === "checkout") {
      var btn = findTierButton(intent.planHandle);
      if (btn) {
        startCheckout(btn);
      }
    } else if (intent.kind === "signup-free") {
      showFreeSignupSuccess();
    }
  }

  function findTierButton(planHandle) {
    var buttons = document.querySelectorAll('[data-action="checkout"]');
    for (var i = 0; i < buttons.length; i += 1) {
      if (buttons[i].dataset.planHandle === planHandle) return buttons[i];
    }
    return null;
  }

  function showFreeSignupSuccess() {
    var card = document.querySelector(
      '.ddd-tier [data-action="signup-free"]'
    );
    if (!card) {
      window.alert(
        "Account created. Browse the catalogue or upgrade any time."
      );
      return;
    }
    var msg = document.createElement("p");
    msg.style.cssText =
      "color:#047857;font-size:.9em;margin:.6rem 0 0;font-weight:600;";
    msg.textContent =
      "Account created. Browse the catalogue or upgrade any time.";
    card.parentNode.insertBefore(msg, card.nextSibling);
    card.disabled = true;
    card.textContent = "Account created";
  }

  // ---------- Inline button state helpers ----------
  function setButtonLoading(btn, loading) {
    if (loading) {
      btn.dataset.originalLabel = btn.textContent;
      btn.disabled = true;
      btn.textContent = "Redirecting...";
    } else {
      btn.disabled = false;
      btn.textContent = btn.dataset.originalLabel || btn.textContent;
    }
  }

  function showInlineError(btn, message) {
    setButtonLoading(btn, false);
    var card = btn.closest(".ddd-tier");
    if (card) {
      var existing = card.querySelector(".ddd-tier__error");
      var errorEl = existing || document.createElement("p");
      errorEl.className = "ddd-tier__error";
      errorEl.style.cssText =
        "color:#c0392b;font-size:.85em;margin:.5rem 0 0;";
      errorEl.textContent = message;
      if (!existing) btn.parentNode.insertBefore(errorEl, btn.nextSibling);
      setTimeout(function () {
        errorEl.remove();
      }, 8000);
    } else {
      window.alert(message);
    }
  }

  // ---------- Checkout flow ----------
  function startCheckout(btn) {
    // Logged-in CTAs: own tier is inert; any other change for a paid member
    // goes through the Stripe portal (so we never double-subscribe them).
    if (btn.dataset.dddCta === "current") return;
    if (btn.dataset.dddCta === "portal") { openPortal(btn); return; }
    var planHandle = btn.dataset.planHandle;
    if (!planHandle) {
      showInlineError(btn, "Plan is not configured for this button.");
      return;
    }
    var interval = getActiveBilling();
    var session = getSession();
    if (!session || !session.accessToken) {
      // Stash intent + open the auth modal. Resume on success.
      openModal({ kind: "checkout", planHandle: planHandle, interval: interval });
      return;
    }

    setButtonLoading(btn, true);
    var card = btn.closest(".ddd-tier");
    if (card) {
      var prev = card.querySelector(".ddd-tier__error");
      if (prev) prev.remove();
    }

    var path = window.location.pathname;
    var membershipPath =
      path.indexOf("/pages/") >= 0 ||
      path.toLowerCase().indexOf("membership") >= 0
        ? path
        : "/pages/membership";
    var membershipPage = window.location.origin + membershipPath;

    var url =
      proxyUrl("/checkout-session") +
      "?plan=" +
      encodeURIComponent(planHandle) +
      "&interval=" +
      encodeURIComponent(interval) +
      "&token=" +
      encodeURIComponent(session.accessToken) +
      "&successUrl=" +
      // After payment, land on the Welcome page (with browse-the-assets
      // links + "you're in"), not back on the pricing table.
      encodeURIComponent(window.location.origin + "/pages/welcome") +
      "&cancelUrl=" +
      encodeURIComponent(membershipPage + "?checkout=cancel") +
      // Charge in the same currency we displayed (Shopify's active currency).
      "&currency=" +
      encodeURIComponent(window.__dddCurrency || "GBP");

    fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
    })
      .then(function (res) {
        return res.json().then(function (payload) {
          return { status: res.status, ok: res.ok, payload: payload };
        });
      })
      .then(function (r) {
        if (!r.ok || !r.payload || !r.payload.success) {
          var msg = (r.payload && r.payload.error) || "Checkout failed.";
          if (r.payload && r.payload.code === "CUSTOMER_ID_MISSING") {
            // Session has expired server-side. Clear + reopen modal.
            clearSession();
            openModal({
              kind: "checkout",
              planHandle: planHandle,
              interval: interval,
            });
            return;
          }
          throw new Error(msg);
        }
        if (!r.payload.url) throw new Error("No redirect URL was returned.");
        window.location.href = r.payload.url;
      })
      .catch(function (err) {
        showInlineError(
          btn,
          (err && err.message) || "Could not start checkout."
        );
      });
  }

  // ---------- Wire up tier buttons ----------
  document
    .querySelectorAll('[data-action="checkout"]')
    .forEach(function (btn) {
      btn.addEventListener("click", function () {
        startCheckout(btn);
      });
    });

  document
    .querySelectorAll('[data-action="signup-free"]')
    .forEach(function (btn) {
      btn.addEventListener("click", function () {
        if (btn.dataset.dddCta === "current") return;
        if (btn.dataset.dddCta === "portal") { openPortal(btn); return; }
        var session = getSession();
        if (session && session.accessToken) {
          // Already signed in, just show the success state, the Free
          // membership bootstraps lazily on first /memberships/status.
          showFreeSignupSuccess();
          return;
        }
        openModal({ kind: "signup-free" });
      });
    });

  // ---------- Expose a tiny API for theme code ----------
  window.dddAuth = {
    getSession: getSession,
    clearSession: clearSession,
    openSignup: function () {
      openModal({ kind: "signup-free" });
    },
    openLogin: function () {
      openModal(null);
      switchTab("login");
    },
  };
})();
